// File: src/ReportsPage.js
import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import "./App.css";
import API from "./config";

/* ==========================================================================
   SECTION 1: DATA EXTRACTION & HELPER FUNCTIONS
   ========================================================================== */

/**
 * Robustly extracts a string value from a row object.
 * It checks multiple potential keys (case-insensitive) to ensure data is found.
 */
const getSafeVal = (row, keys, defaultVal = "UNKNOWN") => {
  for (let k of keys) {
    if (row[k] !== undefined) return String(row[k]).trim();
    const foundKey = Object.keys(row).find(
      (rk) => rk.toUpperCase() === k.toUpperCase()
    );
    if (foundKey) return String(row[foundKey]).trim();
  }
  return defaultVal;
};

// --- Field Specific Wrappers ---

const getSafeState = (row) =>
  getSafeVal(row, ["STATE", "State", "State/UT Name", "State Name"]);
const getSafeUni = (row) =>
  getSafeVal(row, [
    "UNIVERSITY_NAME",
    "University",
    "University/Institute Name",
  ]);
const getSafeYear = (row) =>
  getSafeVal(row, ["FINANCE_YEAR", "Financial Year", "Year", "YEAR"], "");
const getSafeGender = (row) =>
  getSafeVal(row, ["GENDER", "Gender", "Sex"], "").toUpperCase();
const getSafeStream = (row) =>
  getSafeVal(row, ["STREAM", "Stream", "Course", "Discipline"], "General");
const getSafeStudentId = (row) =>
  getSafeVal(row, ["STUDENT_ID", "Student_Id", "ID"], "UNKNOWN");

/**
 * Robustly parses amount fields.
 * Removes commas and returns a raw float.
 */
const getSafeAmount = (row) => {
  const amtKey = Object.keys(row).find((k) => /amt|amount|paid/i.test(k));
  if (!amtKey) return 0;
  let val = row[amtKey];
  if (val === undefined || val === null || val === "") return 0;
  val = String(val).replace(/,/g, "");
  return parseFloat(val) || 0;
};

/**
 * Helper to extract the starting year from a financial year string.
 */
const getYearStart = (fyString) => {
  if (!fyString || fyString === "ALL") return 0;
  const match = String(fyString).match(/(\d{4})/);
  return match ? parseInt(match[1]) : 0;
};

/**
 * Formats a number into Indian Currency format.
 */
const formatCurrency = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "0";
  return num.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
};

/* ==========================================================================
   SECTION 2: API & NETWORK
   ========================================================================== */

const fetchJSON = (url, opts = {}) =>
  fetch(url, opts).then(async (r) => {
    const txt = await r.text();
    try {
      return { ok: r.ok, status: r.status, data: JSON.parse(txt) };
    } catch {
      return { ok: r.ok, status: r.status, data: txt };
    }
  });

/* ==========================================================================
   SECTION 3: MATRIX / PIVOT ENGINE
   ========================================================================== */

const buildColumnTree = (data, levels, currentLevelIndex = 0) => {
  if (currentLevelIndex >= levels.length) return { isLeaf: true };

  const currentKeyType = levels[currentLevelIndex];
  const uniqueValues = new Set();

  data.forEach((row) => {
    let val = "NA";
    if (currentKeyType === "Financial Year") val = getSafeYear(row);
    if (currentKeyType === "Gender") val = getSafeGender(row);
    if (currentKeyType === "Stream") val = getSafeStream(row);
    if (val) uniqueValues.add(val);
  });

  const sortedValues = Array.from(uniqueValues).sort();
  const children = {};

  sortedValues.forEach((val) => {
    const subset = data.filter((row) => {
      let rVal = "NA";
      if (currentKeyType === "Financial Year") rVal = getSafeYear(row);
      if (currentKeyType === "Gender") rVal = getSafeGender(row);
      if (currentKeyType === "Stream") rVal = getSafeStream(row);
      return rVal === val;
    });
    children[val] = buildColumnTree(subset, levels, currentLevelIndex + 1);
  });

  return { isLeaf: false, children, sortedKeys: sortedValues };
};

const getColSpan = (node) => {
  if (node.isLeaf) return 2; // Students + Amount
  let sum = 0;
  node.sortedKeys.forEach((k) => (sum += getColSpan(node.children[k])));
  return sum;
};

/* ==========================================================================
   SECTION 4: MAIN REACT COMPONENT
   ========================================================================== */

export default function ReportsPage({ token }) {
  // ---------------- STATE MANAGEMENT ----------------
  const [lists, setLists] = useState({ years: [] });
  const [rawRows, setRawRows] = useState([]);
  const [matrixData, setMatrixData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  const [fromFy, setFromFy] = useState("ALL");
  const [toFy, setToFy] = useState("ALL");
  const [rowGroup, setRowGroup] = useState("State");
  const [colLevels, setColLevels] = useState(["Financial Year"]);

  // Loading States
  const [listLoading, setListLoading] = useState(false);
  const [listLoadingPercent, setListLoadingPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportPercent, setExportPercent] = useState(0);

  const printRef = useRef();

  // ---------------- CONSTANTS ----------------
  const TARGET_STREAMS = [
    "SCIENCES",
    "ENGINEERING AND TECHNOLOGY",
    "HUMANITIES AND SOCIAL SCIENCES",
  ];

  // ---------------- EFFECTS ----------------
  useEffect(() => {
    let timer;
    if (loading) {
      setLoadingPercent(0);
      timer = setInterval(
        () => setLoadingPercent((prev) => (prev >= 95 ? prev : prev + 3)),
        150
      );
    } else {
      setLoadingPercent(100);
      setTimeout(() => setLoadingPercent(0), 300);
    }
    return () => clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    let timer;
    if (listLoading) {
      setListLoadingPercent(0);
      timer = setInterval(
        () => setListLoadingPercent((prev) => (prev >= 95 ? prev : prev + 10)),
        100
      );
    } else {
      setListLoadingPercent(100);
      setTimeout(() => setListLoadingPercent(0), 500);
    }
    return () => clearInterval(timer);
  }, [listLoading]);

  // ---------------- INITIAL DATA FETCH ----------------
  useEffect(() => {
    async function init() {
      if (!token) return;
      setListLoading(true);
      try {
        const res = await fetchJSON(`${API}/api/pms/lists`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setLists({ years: res.data.yearRange || [] });
      } catch (e) {
        console.error(e);
      } finally {
        setListLoading(false);
      }
    }
    init();
  }, [token]);

  // ---------------- GENERATION LOGIC ----------------

  const handleGenerate = async () => {
    setLoading(true);
    setMatrixData(null);
    setSummaryData(null);

    try {
      const filterBody = { from_fy: fromFy, to_fy: toFy, state: "ALL" };
      const res = await fetchJSON(`${API}/api/pms/filter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(filterBody),
      });

      if (!res.ok) {
        alert("Failed to fetch data");
        setLoading(false);
        return;
      }

      const rows = res.data.rows || [];
      setRawRows(rows);
      processMatrix(rows);
    } catch (e) {
      console.error(e);
      alert("Error generating report");
    } finally {
      setLoading(false);
    }
  };

  const processMatrix = (data) => {
    const fStart = getYearStart(fromFy);
    const tStart = getYearStart(toFy);

    const validData = data.filter((r) => {
      const y = getYearStart(getSafeYear(r));
      if (fStart > 0 && y < fStart) return false;
      if (tStart > 0 && y > tStart) return false;
      return true;
    });

    const effectiveColLevels = colLevels.length > 0 ? colLevels : [];
    const colTree = buildColumnTree(validData, effectiveColLevels);

    const rowGroups = {};
    validData.forEach((r) => {
      let key = "UNKNOWN";
      if (rowGroup === "Financial Year") key = getSafeYear(r);
      else if (rowGroup === "State") key = getSafeState(r);
      else if (rowGroup === "University") key = getSafeUni(r);
      else if (rowGroup === "Gender") key = getSafeGender(r);
      else if (rowGroup === "Stream") key = getSafeStream(r);

      if (!rowGroups[key]) rowGroups[key] = [];
      rowGroups[key].push(r);
    });

    const sortedRowKeys = Object.keys(rowGroups).sort();
    const totalAmt = validData.reduce((acc, r) => acc + getSafeAmount(r), 0);
    const totalStd = new Set(validData.map((r) => getSafeStudentId(r))).size;

    // Calculate Grand Total Stats for the Bottom Row (Gender/Stream Wise)
    const grandTotalStats = getRowStaticStats(validData);

    calculateBottomSummary(validData);

    setMatrixData({
      colTree,
      sortedRowKeys,
      rowGroups,
      effectiveColLevels,
      totalStudents: totalStd,
      totalAmount: totalAmt,
      grandTotalStats,
    });
  };

  const calculateBottomSummary = (activeData) => {
    const getStats = (rows) => ({
      count: new Set(rows.map((r) => getSafeStudentId(r))).size,
      amount: rows.reduce((acc, r) => acc + getSafeAmount(r), 0),
    });

    const maleRows = activeData.filter((r) => getSafeGender(r) === "MALE");
    const femaleRows = activeData.filter((r) => getSafeGender(r) === "FEMALE");

    const genderStats = {
      male: getStats(maleRows),
      female: getStats(femaleRows),
      total: getStats(activeData),
    };

    const streamStats = TARGET_STREAMS.map((streamName) => {
      const sRows = activeData.filter(
        (r) => getSafeStream(r).toUpperCase().trim() === streamName
      );
      const mRows = sRows.filter((r) => getSafeGender(r) === "MALE");
      const fRows = sRows.filter((r) => getSafeGender(r) === "FEMALE");

      return {
        name: streamName,
        male: getStats(mRows),
        female: getStats(fRows),
        total: getStats(sRows),
      };
    });

    setSummaryData({ genderStats, streamStats });
  };

  /**
   * Helper to calculate static column data for a SINGLE ROW (e.g., specific State/University)
   */
  const getRowStaticStats = (rowData) => {
    const getS = (rows) => ({
      c: new Set(rows.map((r) => getSafeStudentId(r))).size,
      a: rows.reduce((acc, r) => acc + getSafeAmount(r), 0),
    });

    // Gender Wise Stats for this Row
    const maleRows = rowData.filter((r) => getSafeGender(r) === "MALE");
    const femRows = rowData.filter((r) => getSafeGender(r) === "FEMALE");

    const gender = {
      m: getS(maleRows),
      f: getS(femRows),
      t: getS(rowData), // This 't' is effectively the ROW GRAND TOTAL
    };

    // Stream Wise Stats for this Row
    const streams = TARGET_STREAMS.map((sName) => {
      const sRows = rowData.filter(
        (r) => getSafeStream(r).toUpperCase().trim() === sName
      );
      const mRows = sRows.filter((r) => getSafeGender(r) === "MALE");
      const fRows = sRows.filter((r) => getSafeGender(r) === "FEMALE");
      return {
        name: sName,
        male: getS(mRows),
        female: getS(fRows),
        total: getS(sRows),
      };
    });

    return { gender, streams };
  };

  // ---------------- EXPORT & PRINT HANDLERS ----------------

  const handleDownloadExcel = async () => {
    if (!matrixData || !printRef.current) return;

    setIsExporting(true);
    setExportPercent(10);

    await new Promise((r) => setTimeout(r, 100));
    setExportPercent(30);

    try {
      const tableElt = printRef.current.querySelector("table");

      if (!tableElt) {
        alert("No table found to export.");
        setIsExporting(false);
        setExportPercent(0);
        return;
      }

      setExportPercent(50);

      const wb = XLSX.utils.table_to_book(tableElt, { sheet: "NFSC Report" });

      setExportPercent(80);

      XLSX.writeFile(wb, `NFSC_Report_${Date.now()}.xlsx`);

      setExportPercent(100);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export Excel.");
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setExportPercent(0);
      }, 500);
    }
  };

  // ---------------- RENDER HELPERS ----------------

  const renderHeaderRows = (node, depth = 0, rows = []) => {
    if (node.isLeaf) return rows;
    if (!rows[depth]) rows[depth] = [];

    node.sortedKeys.forEach((key) => {
      const childNode = node.children[key];
      const span = getColSpan(childNode);
      rows[depth].push(
        <th key={`${depth}-${key}`} colSpan={span} className="matrix-th">
          {key}
        </th>
      );
      renderHeaderRows(childNode, depth + 1, rows);
    });

    // Determine Visibility of Right-Side Statics
    const showGenderStatic =
      (rowGroup === "State" || rowGroup === "University") &&
      colLevels.includes("Gender");
    const showStreamStatic =
      (rowGroup === "State" || rowGroup === "University") &&
      colLevels.includes("Stream");

    // Only inject headers on the TOP ROW (depth 0)
    if (depth === 0) {
      const totalHeaderRows = matrixData.effectiveColLevels.length;
      const rowSpan = totalHeaderRows || 1;

      // 1. GENDER STATIC HEADER
      if (showGenderStatic) {
        rows[depth].push(
          <th
            key="static-gender-head"
            colSpan={6}
            rowSpan={rowSpan}
            className="matrix-th static-head-blue"
          >
            Total Gender Wise
          </th>
        );
      }

      // 2. STREAM STATIC HEADER
      if (showStreamStatic) {
        rows[depth].push(
          <th
            key="static-stream-head"
            colSpan={TARGET_STREAMS.length * 6}
            rowSpan={rowSpan}
            className="matrix-th static-head-green"
          >
            Total Stream Wise
          </th>
        );
      }

      // 3. GLOBAL GRAND TOTAL HEADER (ALWAYS VISIBLE)
      rows[depth].push(
        <th
          key="static-grand-total-head"
          colSpan={2}
          rowSpan={rowSpan}
          className="matrix-th"
          style={{
            backgroundColor: "#2c3e50",
            color: "#fff",
            borderLeft: "2px solid #000",
          }}
        >
          Grand Total
        </th>
      );
    }

    return rows;
  };

  const getLeafPaths = (node, currentPath = []) => {
    if (node.isLeaf) return [currentPath];
    let paths = [];
    node.sortedKeys.forEach((k) => {
      paths = [
        ...paths,
        ...getLeafPaths(node.children[k], [...currentPath, k]),
      ];
    });
    return paths;
  };

  const toggleColLevel = (val) => {
    setColLevels((prev) =>
      prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
    );
  };

  // ---------------- UI RENDER ----------------
  return (
    <div className="report-container">
      {/* FILTER CARD */}
      <div className="card filters-card no-print">
        <div
          style={{
            height: listLoadingPercent > 0 ? "4px" : "0px",
            background: "#e0e0e0",
            transition: "height 0.2s",
            overflow: "hidden",
            marginBottom: listLoadingPercent > 0 ? 10 : 0,
          }}
        >
          <div
            style={{
              width: `${listLoadingPercent}%`,
              height: "100%",
              background: "#28a745",
              transition: "width 0.2s ease",
            }}
          />
        </div>

        {listLoading && (
          <div
            style={{
              fontSize: "12px",
              color: "#28a745",
              marginBottom: 10,
              textAlign: "center",
            }}
          >
            Fetching Data...
          </div>
        )}

        <div className="header-row">
          <h2 className="report-title">NFSC: Physical and Financial Report</h2>
        </div>

        <div className="controls-grid">
          <div className="control-group">
            <label>From Financial Year</label>
            <select
              value={fromFy}
              onChange={(e) => setFromFy(e.target.value)}
              className="gov-select"
            >
              <option value="ALL">ALL</option>
              {lists.years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="control-group">
            <label>To Financial Year</label>
            <select
              value={toFy}
              onChange={(e) => setToFy(e.target.value)}
              className="gov-select"
            >
              <option value="ALL">ALL</option>
              {lists.years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="control-group">
            <label>Group Rows By</label>
            <select
              value={rowGroup}
              onChange={(e) => setRowGroup(e.target.value)}
              className="gov-select highlight"
            >
              <option value="Financial Year">Financial Year</option>
              <option value="State">State</option>
              <option value="University">University</option>
              <option value="Gender">Gender</option>
              <option value="Stream">Stream</option>
            </select>
          </div>
        </div>
        <div className="control-group">
          <label>Split Columns By</label>
          <div className="dropdown-check-list">
            {["Financial Year", "Gender", "Stream"].map((opt) => (
              <label key={opt} className="check-label">
                <input
                  type="checkbox"
                  checked={colLevels.includes(opt)}
                  onChange={() => toggleColLevel(opt)}
                  disabled={rowGroup === opt}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
        <div className="action-row">
          <button
            className="btn primary"
            onClick={handleGenerate}
            disabled={loading || isExporting}
          >
            {loading ? "Generating..." : " Generate"}
          </button>
        </div>

        {/* GENERATION LOADING BAR (BLUE) */}
        {loading && (
          <div className="inline-progress" style={{ marginTop: 15 }}>
            <div
              style={{
                marginBottom: 6,
                fontSize: "0.9rem",
                color: "#1976d2",
                textAlign: "center",
                fontWeight: 600,
              }}
            >
              Generating Report... {loadingPercent}%
            </div>

            <div
              style={{
                width: "100%",
                height: 10,
                background: "#e3f2fd",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${loadingPercent}%`,
                  height: "100%",
                  background: "#1976d2",
                  transition: "width 0.25s ease",
                }}
              />
            </div>
          </div>
        )}

        {/* EXPORT LOADING BAR (ORANGE) */}
        {isExporting && (
          <div className="inline-progress" style={{ marginTop: 15 }}>
            <div
              style={{ marginBottom: 6, fontSize: "0.9rem", color: "#e65100" }}
            >
              <strong>Exporting Excel... {exportPercent}%</strong>
            </div>
            <div
              style={{
                width: "100%",
                height: 8,
                background: "#ffe0b2",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${exportPercent}%`,
                  height: "100%",
                  background: "#ff9800",
                  transition: "width 0.2s ease",
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* RESULTS CARD */}
      {matrixData && (
        <div className="card results-card" ref={printRef}>
          <div
            className="result-header-row no-print"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
              padding: "0 5px",
            }}
          >
            <h3 style={{ margin: 0, color: "#333", fontSize: "1.1rem" }}>
              Generated Report
            </h3>
            <div
              className="result-actions"
              style={{ display: "flex", gap: "10px" }}
            >
              <button
                className="btn outline sm"
                onClick={handleDownloadExcel}
                disabled={isExporting}
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <span>
                  📊 {isExporting ? "Exporting..." : "Export Excel"}
                </span>
              </button>
            </div>
          </div>

          <div className="summary-bar">
            <span>
              Financial Year: <b>{fromFy}</b> to <b>{toFy}</b>
            </span>
            <span className="divider">|</span>
            <span>
              Total Students: <b>{matrixData.totalStudents}</b>
            </span>
            <span className="divider">|</span>
            <span>
              Total Amount: <b>₹ {formatCurrency(matrixData.totalAmount)}</b>
            </span>
          </div>

          <div className="table-responsive">
            <table className="matrix-table">
              <thead>
                {(() => {
                  let headerRows = renderHeaderRows(matrixData.colTree);
                  if (headerRows.length === 0) headerRows = [[]];
                  const depth = headerRows.length;

                  return headerRows.map((row, i) => (
                    <tr key={i}>
                      {i === 0 && (
                        <th
                          rowSpan={depth + 1}
                          className="matrix-th sticky-col"
                          style={{ zIndex: 10 }}
                        >
                          {rowGroup.toUpperCase()}
                        </th>
                      )}
                      {row}
                    </tr>
                  ));
                })()}

                {/* METRICS ROW */}
                <tr>
                  {getLeafPaths(matrixData.colTree).map((_, i) => (
                    <React.Fragment key={i}>
                      <th className="metric-th">Students</th>
                      <th className="metric-th">Amount (₹)</th>
                    </React.Fragment>
                  ))}

                  {/* STATIC COLUMNS - SUB HEADERS */}
                  {(() => {
                    const showGenderStatic =
                      (rowGroup === "State" || rowGroup === "University") &&
                      colLevels.includes("Gender");
                    const showStreamStatic =
                      (rowGroup === "State" || rowGroup === "University") &&
                      colLevels.includes("Stream");
                    return (
                      <>
                        {showGenderStatic && (
                          <>
                            <th className="metric-th static-th">Total Male</th>
                            <th className="metric-th static-th">
                              Total Male Amount
                            </th>
                            <th className="metric-th static-th">
                              Total Female
                            </th>
                            <th className="metric-th static-th">
                              Total Female Amount
                            </th>
                            <th className="metric-th static-th">
                              Total Student
                            </th>
                            <th className="metric-th static-th">
                              Total Amount
                            </th>
                          </>
                        )}

                        {showStreamStatic && (
                          <>
                            {TARGET_STREAMS.map((s) => (
                              <React.Fragment key={s}>
                                <th
                                  className="metric-th static-th-green"
                                  colSpan={6}
                                  title={s}
                                >
                                  {s}
                                </th>
                              </React.Fragment>
                            ))}
                          </>
                        )}

                        {/* GRAND TOTAL COLUMNS HEADERS */}
                        <th
                          className="metric-th"
                          style={{
                            backgroundColor: "#34495e",
                            color: "#fff",
                            borderLeft: "2px solid #000",
                          }}
                        >
                          Students
                        </th>
                        <th
                          className="metric-th"
                          style={{ backgroundColor: "#34495e", color: "#fff" }}
                        >
                          Amount (₹)
                        </th>
                      </>
                    );
                  })()}
                </tr>

                {/* OPTIONAL: EXTRA ROW FOR STREAM BREAKDOWN LABELS */}
                {(() => {
                  const showStreamStatic =
                    (rowGroup === "State" || rowGroup === "University") &&
                    colLevels.includes("Stream");
                  if (!showStreamStatic) return null;

                  return (
                    <tr>
                      <th className="sticky-col"></th>
                      {/* Empty dynamic columns */}
                      {getLeafPaths(matrixData.colTree).map((_, i) => (
                        <React.Fragment key={i}>
                          <th></th>
                          <th></th>
                        </React.Fragment>
                      ))}

                      {/* Empty Gender if visible */}
                      {(rowGroup === "State" || rowGroup === "University") &&
                        colLevels.includes("Gender") && <th colSpan={6}></th>}

                      {/* Stream Sub-Headers */}
                      {TARGET_STREAMS.map((s) => (
                        <React.Fragment key={s}>
                          <th className="sub-metric-th">Male</th>
                          <th className="sub-metric-th">Male Amount</th>
                          <th className="sub-metric-th">Female</th>
                          <th className="sub-metric-th">Female Amount</th>
                          <th className="sub-metric-th">Total</th>
                          <th className="sub-metric-th">Total Amount</th>
                        </React.Fragment>
                      ))}

                      {/* Empty Grand Total cols for this row */}
                      <th
                        colSpan={2}
                        style={{ borderLeft: "2px solid #000" }}
                      ></th>
                    </tr>
                  );
                })()}
              </thead>

              <tbody>
                {matrixData.sortedRowKeys.map((rKey) => {
                  const rowData = matrixData.rowGroups[rKey];
                  const paths = getLeafPaths(matrixData.colTree);
                  const stats = getRowStaticStats(rowData);
                  const showGenderStatic =
                    (rowGroup === "State" || rowGroup === "University") &&
                    colLevels.includes("Gender");
                  const showStreamStatic =
                    (rowGroup === "State" || rowGroup === "University") &&
                    colLevels.includes("Stream");

                  // Grand Total Data for the Row (Already calculated in stats.gender.t)
                  const rowGrandTotal = stats.gender.t;

                  return (
                    <tr key={rKey}>
                      <td className="sticky-col row-label">
                        <b>{rKey}</b>
                      </td>

                      {paths.map((path, pIdx) => {
                        const cellSubset = rowData.filter((r) => {
                          return path.every((val, lvlIdx) => {
                            const type = matrixData.effectiveColLevels[lvlIdx];
                            let rVal = "";
                            if (type === "Financial Year")
                              rVal = getSafeYear(r);
                            if (type === "Gender") rVal = getSafeGender(r);
                            if (type === "Stream") rVal = getSafeStream(r);
                            return rVal === val;
                          });
                        });
                        const uIds = new Set(
                          cellSubset.map((x) => getSafeStudentId(x))
                        );
                        const amt = cellSubset.reduce(
                          (acc, x) => acc + getSafeAmount(x),
                          0
                        );
                        return (
                          <React.Fragment key={pIdx}>
                            <td className="val-cell">
                              {uIds.size > 0 ? uIds.size : 0}
                            </td>
                            <td className="val-cell">
                              {amt > 0 ? formatCurrency(amt) : 0}
                            </td>
                          </React.Fragment>
                        );
                      })}

                      {/* STATIC CELLS */}
                      {showGenderStatic && (
                        <>
                          <td className="val-cell bg-blue-light">
                            {stats.gender.m.c}
                          </td>
                          <td className="val-cell bg-blue-light">
                            {formatCurrency(stats.gender.m.a)}
                          </td>
                          <td className="val-cell bg-blue-light">
                            {stats.gender.f.c}
                          </td>
                          <td className="val-cell bg-blue-light">
                            {formatCurrency(stats.gender.f.a)}
                          </td>
                          <td className="val-cell bg-blue-bold">
                            {stats.gender.t.c}
                          </td>
                          <td className="val-cell bg-blue-bold">
                            {formatCurrency(stats.gender.t.a)}
                          </td>
                        </>
                      )}

                      {showStreamStatic &&
                        stats.streams.map((s, idx) => (
                          <React.Fragment key={idx}>
                            <td className="val-cell">{s.male.c}</td>
                            <td className="val-cell">
                              {formatCurrency(s.male.a)}
                            </td>
                            <td className="val-cell">{s.female.c}</td>
                            <td className="val-cell">
                              {formatCurrency(s.female.a)}
                            </td>
                            <td className="val-cell bg-gray-bold">
                              {s.total.c}
                            </td>
                            <td className="val-cell bg-gray-bold">
                              {formatCurrency(s.total.a)}
                            </td>
                          </React.Fragment>
                        ))}

                      {/* ROW GRAND TOTAL CELLS */}
                      <td
                        className="val-cell"
                        style={{
                          fontWeight: "bold",
                          borderLeft: "2px solid #000",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        {rowGrandTotal.c}
                      </td>
                      <td
                        className="val-cell"
                        style={{
                          fontWeight: "bold",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        {formatCurrency(rowGrandTotal.a)}
                      </td>
                    </tr>
                  );
                })}

                {/* GRAND TOTAL ROW */}
                <tr className="total-row">
                  <td className="sticky-col">GRAND TOTAL</td>

                  {getLeafPaths(matrixData.colTree).map((path, pIdx) => {
                    const colSubset = rawRows.filter((r) => {
                      const y = getYearStart(getSafeYear(r));
                      const fStart = getYearStart(fromFy);
                      const tStart = getYearStart(toFy);
                      if (fStart > 0 && y < fStart) return false;
                      if (tStart > 0 && y > tStart) return false;
                      return path.every((val, lvlIdx) => {
                        const type = matrixData.effectiveColLevels[lvlIdx];
                        let rVal = "";
                        if (type === "Financial Year") rVal = getSafeYear(r);
                        if (type === "Gender") rVal = getSafeGender(r);
                        if (type === "Stream") rVal = getSafeStream(r);
                        return rVal === val;
                      });
                    });
                    const uIds = new Set(
                      colSubset.map((x) => getSafeStudentId(x))
                    );
                    const amt = colSubset.reduce(
                      (acc, x) => acc + getSafeAmount(x),
                      0
                    );
                    return (
                      <React.Fragment key={pIdx}>
                        <td>{uIds.size}</td>
                        <td>{formatCurrency(amt)}</td>
                      </React.Fragment>
                    );
                  })}

                  {/* STATIC GRAND TOTALS */}
                  {(() => {
                    const showGenderStatic =
                      (rowGroup === "State" || rowGroup === "University") &&
                      colLevels.includes("Gender");
                    const showStreamStatic =
                      (rowGroup === "State" || rowGroup === "University") &&
                      colLevels.includes("Stream");

                    const gStats = matrixData.grandTotalStats;

                    return (
                      <>
                        {showGenderStatic && (
                          <>
                            <td>{gStats.gender.m.c}</td>
                            <td>{formatCurrency(gStats.gender.m.a)}</td>
                            <td>{gStats.gender.f.c}</td>
                            <td>{formatCurrency(gStats.gender.f.a)}</td>
                            <td>{gStats.gender.t.c}</td>
                            <td>{formatCurrency(gStats.gender.t.a)}</td>
                          </>
                        )}
                        {showStreamStatic &&
                          gStats.streams.map((s, idx) => (
                            <React.Fragment key={idx}>
                              <td>{s.male.c}</td>
                              <td>{formatCurrency(s.male.a)}</td>
                              <td>{s.female.c}</td>
                              <td>{formatCurrency(s.female.a)}</td>
                              <td>{s.total.c}</td>
                              <td>{formatCurrency(s.total.a)}</td>
                            </React.Fragment>
                          ))}

                        {/* GLOBAL GRAND TOTAL CELLS */}
                        <td
                          style={{
                            fontWeight: "bold",
                            borderLeft: "2px solid #000",
                            backgroundColor: "#ddd",
                          }}
                        >
                          {matrixData.totalStudents}
                        </td>
                        <td
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#ddd",
                          }}
                        >
                          {formatCurrency(matrixData.totalAmount)}
                        </td>
                      </>
                    );
                  })()}
                </tr>
              </tbody>
            </table>
          </div>

          {/* BOTTOM SUMMARY TABLES */}
          {summaryData && (
            <div
              className="bottom-summary-section"
              style={{
                marginTop: "30px",
                padding: "15px",
                borderTop: "2px solid #ddd",
              }}
            >
              {(rowGroup === "State" || rowGroup === "University") &&
                colLevels.includes("Gender") && (
                  <>
                    <h3
                      style={{
                        fontSize: "16px",
                        color: "#444",
                        marginBottom: "10px",
                      }}
                    >
                      Total Gender Wise
                    </h3>
                    <table
                      className="matrix-table summary-table"
                      style={{ width: "100%", marginBottom: "25px" }}
                    >
                      <thead>
                        <tr style={{ background: "#f8f9fa" }}>
                          <th>Total Male</th>
                          <th>Total Male Amount</th>
                          <th>Total Female</th>
                          <th>Total Female Amount</th>
                          <th>Total Student</th>
                          <th>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{summaryData.genderStats.male.count}</td>
                          <td>
                            {formatCurrency(
                              summaryData.genderStats.male.amount
                            )}
                          </td>
                          <td>{summaryData.genderStats.female.count}</td>
                          <td>
                            {formatCurrency(
                              summaryData.genderStats.female.amount
                            )}
                          </td>
                          <td style={{ fontWeight: "bold" }}>
                            {summaryData.genderStats.total.count}
                          </td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(
                              summaryData.genderStats.total.amount
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}

              {(rowGroup === "State" || rowGroup === "University") &&
                colLevels.includes("Stream") && (
                  <>
                    <h3
                      style={{
                        fontSize: "16px",
                        color: "#444",
                        marginBottom: "10px",
                      }}
                    >
                      Total Stream Wise
                    </h3>
                    <table
                      className="matrix-table summary-table"
                      style={{ width: "100%" }}
                    >
                      <thead>
                        <tr style={{ background: "#f8f9fa" }}>
                          <th style={{ width: "30%", textAlign: "left" }}>
                            Stream Name
                          </th>
                          <th>Total Male</th>
                          <th>Male Amount</th>
                          <th>Total Female</th>
                          <th>Female Amount</th>
                          <th>Total Student</th>
                          <th>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.streamStats.map((stream, idx) => (
                          <tr key={idx}>
                            <td
                              style={{ textAlign: "left", fontWeight: "500" }}
                            >
                              {stream.name}
                            </td>
                            <td>{stream.male.count}</td>
                            <td>{formatCurrency(stream.male.amount)}</td>
                            <td>{stream.female.count}</td>
                            <td>{formatCurrency(stream.female.amount)}</td>
                            <td>{stream.total.count}</td>
                            <td>{formatCurrency(stream.total.amount)}</td>
                          </tr>
                        ))}
                        <tr
                          style={{
                            background: "#f0f0f0",
                            fontWeight: "bold",
                          }}
                        >
                          <td style={{ textAlign: "left" }}>GRAND TOTAL</td>
                          <td>
                            {summaryData.streamStats.reduce(
                              (sum, item) => sum + item.male.count,
                              0
                            )}
                          </td>
                          <td>
                            {formatCurrency(
                              summaryData.streamStats.reduce(
                                (sum, item) => sum + item.male.amount,
                                0
                              )
                            )}
                          </td>
                          <td>
                            {summaryData.streamStats.reduce(
                              (sum, item) => sum + item.female.count,
                              0
                            )}
                          </td>
                          <td>
                            {formatCurrency(
                              summaryData.streamStats.reduce(
                                (sum, item) => sum + item.female.amount,
                                0
                              )
                            )}
                          </td>
                          <td>
                            {summaryData.streamStats.reduce(
                              (sum, item) => sum + item.total.count,
                              0
                            )}
                          </td>
                          <td>
                            {formatCurrency(
                              summaryData.streamStats.reduce(
                                (sum, item) => sum + item.total.amount,
                                0
                              )
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}

              <div
                style={{
                  margin: "20px 0",
                  textAlign: "center",
                  color: "#888",
                }}
              >
                -----------
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}